import clsx from "clsx";
import { useTranslation } from "react-i18next";

import SelectedCheckbox from "@/assets/ux/SelectedCheckbox.svg?react";

/**
 * Select-all control shared by the portal's selectable tables.
 * It lives in the table header row on desktop and next to the card headline on
 * mobile, where the card list has no header row.
 *
 * Selection is passed in and out as plain id arrays so the component stays
 * independent of how each table's form stores it. `selectedIds` is the full
 * selection across all pages, `currentPageIds` only what is on screen: the
 * checkbox reflects and toggles the current page, but `onChange` hands back the
 * whole selection so selections on other pages survive paging.
 */
export const TableSelectAllCheckbox = ({
  currentPageIds,
  selectedIds,
  onChange,
  dataTestid,
  labels,
}: {
  currentPageIds: string[];
  selectedIds: string[];
  onChange: (nextSelectedIds: string[]) => void;
  dataTestid: string;
  /** Defaults to the invoice wording of the bill tables. */
  labels?: { selectAll: string; deselectAll: string };
}) => {
  const { t } = useTranslation("portal");

  const currentPageSelectionCount = selectedIds.filter((id) =>
    currentPageIds.includes(id),
  ).length;

  const hasSelection = currentPageSelectionCount > 0;
  const allSelected =
    hasSelection && currentPageSelectionCount === currentPageIds.length;

  const toggleAll = () =>
    onChange(
      allSelected
        ? selectedIds.filter((id) => !currentPageIds.includes(id))
        : [...new Set([...selectedIds, ...currentPageIds])],
    );

  // Keyed off `allSelected`, like `toggleAll` — in the indeterminate state the
  // click selects the rest, so labelling it "deselect all" would be backwards.
  const label = labels
    ? labels[allSelected ? "deselectAll" : "selectAll"]
    : t(
        allSelected
          ? "bills.list.tableHeaders.deselectAllBills"
          : "bills.list.tableHeaders.selectAllBills",
      );

  return (
    <button
      data-testid={dataTestid}
      className={clsx(
        "flex size-6 shrink-0 items-center justify-center rounded-md transition-colors",
        hasSelection
          ? "bg-surface-primary text-foreground-primary-inverse"
          : "border-2 border-border-secondary hover:border-border-primary",
      )}
      type="button"
      title={label}
      // `title` alone is an unreliable accessible name on a custom role.
      aria-label={label}
      aria-checked={allSelected ? "true" : hasSelection ? "mixed" : "false"}
      role="checkbox"
      onClick={toggleAll}
    >
      {allSelected ? (
        <SelectedCheckbox className="size-6" />
      ) : (
        hasSelection && (
          <span className="h-px w-3 bg-current" aria-hidden="true" />
        )
      )}
    </button>
  );
};
